import { ChartBarIcon, ShoppingBagIcon, UsersIcon, CurrencyDollarIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/outline";
import { fetchDashboardDetail, fetchUserOrders } from "../services/user";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, ClockIcon, TruckIcon, ClockArrowUpIcon, BanIcon, Contact, Contact2Icon } from "lucide-react";

const SellerDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [orderMetrics, setOrderMetrics] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersPerPage] = useState(4);
  const navigate = useNavigate();
  // Map status to icons
  const statusIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    'Completed': CheckCircleIcon,
    'In-progress': TruckIcon,
    'Pending': ClockIcon,
    'Accepted': ClockArrowUpIcon,
    'Cancelled': BanIcon
  };

  // Fetch products from API
  useEffect(() => {
    const userId = localStorage.getItem('ud-user') ?? '';
    setIsLoading(true);
    const fetchDashboardDetails = async () => {
      try {
        setError(null);

        // Replace with your actual API endpoint
        const response: any = await fetchDashboardDetail(userId);
        if (!response.success) {
          throw new Error('Failed to fetch details');
        }
        const data = await response.data ?? [];
        console.log(data);
        setOrderMetrics(data);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    const fetchOrderDetails = async () => {
      try {
        setError(null);
        // Replace with your actual API endpoint
        const response: any = await fetchUserOrders({
          params: {
            page: 1,
            limit: 4,
            search: '',
            userId: userId
          }
        });
        if (!response.success) {
          throw new Error('Failed to fetch details');
        }
        const data = await response.data ?? [];
        setRecentOrders(data);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    // fetchOrderDetails();
    fetchDashboardDetails();
  }, []);

  const quickActions = [
    { name: 'Add New Product', href: '/products/add', icon: ShoppingBagIcon },
    { name: 'View Orders', href: '/orders', icon: ChartBarIcon },
    { name: 'Contact Us', href: '/contact-us', icon: Contact2Icon },
    { name: 'Seller Insights', href: '/account', icon: UsersIcon },
  ];

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">Overview of your store performance and activity</p>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {orderMetrics?.map((item: any) => {
          const StatusIcon = statusIcons[item.status] || ChartBarIcon;
          return (
            <div key={item.status} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <StatusIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{item.status} Orders</dt>
                      <dd>
                        <div className="text-lg font-medium text-gray-900">{item.count}</div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Recent Orders */}
        <div className="bg-white shadow rounded-lg lg:col-span-2">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Recent Orders</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOrders.map((order: any) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.customer}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                          order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-4 sm:px-6 border-t border-gray-200">
            <a href="#" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              View all orders<span aria-hidden="true"> &rarr;</span>
            </a>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {quickActions.map((action) => (
                <div
                  key={action.name}
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                >
                  <div className="flex-shrink-0">
                    <action.icon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <a href={action.href} className="focus:outline-none">
                      <span className="absolute inset-0" aria-hidden="true" />
                      <p className="text-sm font-medium text-gray-900">{action.name}</p>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Chart (placeholder) */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Performance Overview</h3>
        <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-gray-500">Sales chart will appear here</p>
        </div>
      </div>
    </div>
  );
}


export default SellerDashboard;